import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaCheckCircle, FaSpinner } from 'react-icons/fa';

export default function Contact() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    axios.get('/api/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post('/api/contact', form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrMsg(err.response?.data?.message || 'Bir hata oluştu, lütfen tekrar deneyin.');
    }
  };

  const mapUrl = settings.map_embed_url || 'https://www.openstreetmap.org/export/embed.html?bbox=32.70,39.90,32.80,39.95&layer=mapnik';

  return (
    <div className="pt-20">
      {/* Header */}
      <div className="hero-gradient hero-pattern py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-secondary">Ana Sayfa</Link>
            <span className="mx-2">/</span><span className="text-white">İletişim</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">İletişim</h1>
          <p className="text-gray-300 text-lg">Sorularınız için bize ulaşın</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Left: Info */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-primary mb-2">Bize Ulaşın</h2>
              <p className="text-gray-500 text-sm">Sorularınız, teklifleriniz veya işbirliği için iletişime geçin.</p>
            </div>

            {[
              { icon: FaPhone, label: 'Telefon', value: settings.company_phone || '+90 312 000 00 00', href: `tel:${settings.company_phone}` },
              { icon: FaEnvelope, label: 'E-posta', value: settings.company_email || 'info@enesplastik.com', href: `mailto:${settings.company_email}` },
              { icon: FaMapMarkerAlt, label: 'Adres', value: settings.company_address || 'Organize Sanayi Bölgesi, Ankara', href: null },
            ].map(item => (
              <div key={item.label} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="text-secondary" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} className="text-primary font-medium hover:text-secondary transition-colors">{item.value}</a>
                  ) : (
                    <p className="text-primary font-medium">{item.value}</p>
                  )}
                </div>
              </div>
            ))}

            {/* WhatsApp Button */}
            {settings.whatsapp_number && (
              <a
                href={`https://wa.me/${settings.whatsapp_number}?text=Merhaba, bilgi almak istiyorum.`}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-semibold px-5 py-4 rounded-xl transition-colors w-full"
              >
                <FaWhatsapp size={22} />
                <div>
                  <div className="text-sm">WhatsApp ile İletişim</div>
                  <div className="text-xs text-green-100">Hemen yazın, hızlı cevap alın</div>
                </div>
              </a>
            )}
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-2">
            <div className="card p-8">
              <h3 className="text-xl font-bold text-primary mb-6">Mesaj Gönderin</h3>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <FaCheckCircle className="text-green-500 text-3xl" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-700 mb-2">Mesajınız Alındı!</h4>
                  <p className="text-gray-500">En kısa sürede size dönüş yapacağız.</p>
                  <button onClick={() => setStatus(null)} className="mt-6 text-secondary font-medium hover:underline">
                    Yeni Mesaj Gönder
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Ad Soyad *</label>
                      <input name="name" value={form.name} onChange={handleChange} required
                        placeholder="Adınız Soyadınız" className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">E-posta *</label>
                      <input name="email" type="email" value={form.email} onChange={handleChange} required
                        placeholder="email@ornek.com" className="input-field" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefon</label>
                      <input name="phone" type="tel" value={form.phone} onChange={handleChange}
                        placeholder="+90 5XX XXX XX XX" className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Konu</label>
                      <input name="subject" value={form.subject} onChange={handleChange}
                        placeholder="Mesaj konusu" className="input-field" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Mesajınız *</label>
                    <textarea name="message" value={form.message} onChange={handleChange} required rows={5}
                      placeholder="Mesajınızı buraya yazın..." className="input-field resize-none" />
                  </div>

                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 text-sm">{errMsg}</div>
                  )}

                  <button type="submit" disabled={status === 'loading'}
                    className="btn-primary w-full justify-center py-4 disabled:opacity-60 disabled:cursor-not-allowed">
                    {status === 'loading' ? (
                      <><FaSpinner className="animate-spin" /> Gönderiliyor...</>
                    ) : 'Mesaj Gönder'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-lg">
          <iframe
            src={mapUrl}
            title="Harita"
            className="w-full h-80 border-0"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
}
